import { SAUNA_ENQUIRY_OPTIONS } from "@/data/sauna-enquiry-options";
import { useState } from "react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { trackEvent } from "@/hooks/use-analytics";
import { Phone, Mail, Clock, Send, Shield, Zap, MapPin, Wrench, Users, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import FileUploader, { type UploadedFile } from "@/components/file-uploader";

const labelCls = "text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500";
const fieldCls = "h-12 rounded-xl border-slate-200 bg-slate-50/70 focus-visible:bg-white";

export default function Contact() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const updateForm = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          formType: `Contact — ${formData.subject || "General Enquiry"}`,
          attachments: files.map(f => ({ name: f.name, mimeType: f.mimeType, data: f.data })),
        }),
      });
      if (!res.ok) throw new Error("Failed to send");
      toast({
        title: "Message sent!",
        description: "We'll get back to you within the hour.",
      });
      setFormData({ name: "", phone: "", email: "", subject: "", message: "" });
      setFiles([]);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us on 020 8087 0906.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: <Phone className="w-5 h-5" />, title: "Phone", detail: "020 8087 0906", href: "tel:02080870906", track: true },
    { icon: <Mail className="w-5 h-5" />, title: "Email", detail: "info@vertexclimate.co.uk", href: "mailto:info@vertexclimate.co.uk", track: false },
    { icon: <MapPin className="w-5 h-5" />, title: "Address", detail: "13 St. Swithin's Lane, 2nd Floor, London, EC4N 8AL", href: null, track: false },
    { icon: <Clock className="w-5 h-5" />, title: "Hours", detail: "Mon-Fri: 8:45am-5pm, Emergency: 24/7", href: null, track: false },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <main className="flex-1 bg-[#f3f6fb]">
        {/* Hero: team photo + intro */}
        <section className="relative overflow-hidden bg-[#0b1d3a] text-white">
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
          <div className="relative container mx-auto px-4 max-w-7xl py-12 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> Contact VertexAir
              </span>
              <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-bold font-display leading-[1.02] tracking-tight">
                Talk to the people <span className="text-sky-400">who do the work.</span>
              </h1>
              <p className="mt-5 text-lg text-white/75 max-w-lg">
                Get in touch with the VertexAir team. We're here to help with air conditioning, HVAC and sauna projects for homes and businesses.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href="tel:02080870906" onClick={() => trackEvent("call_click", "Contact Hero")} data-testid="link-hero-call"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 transition-colors px-6 h-12 font-semibold text-[#0b1d3a]">
                  <Phone className="w-4 h-4" /> 020 8087 0906
                </a>
                <a href="#contact-form" data-testid="link-hero-form"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 hover:bg-white/10 transition-colors px-6 h-12 font-semibold">
                  Send a message <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <figure className="lg:col-span-7 relative">
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/40">
                <img
                  src={`${import.meta.env.BASE_URL}images/vertexair-team-vans.webp`}
                  alt="Three VertexAir engineers in branded uniforms standing in front of VertexAir vans and office"
                  width={1024}
                  height={683}
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover object-[50%_40%]"
                  data-testid="img-team"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0b1d3a]/85 to-transparent" />
                <figcaption className="absolute left-4 bottom-4 right-4 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold">The VertexAir team</span>
                  <span className="hidden sm:inline text-xs text-white/75">Climate &amp; sauna engineering</span>
                </figcaption>
              </div>
            </figure>
          </div>
        </section>

        {/* Contact strip */}
        <section className="relative z-10 -mt-px">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-2xl bg-slate-200 shadow-sm ring-1 ring-slate-200 mt-6 lg:-mt-6">
              {contactItems.map((item, i) => (
                <div key={i} className="bg-white p-5 flex items-start gap-4" data-testid={`card-contact-${item.title.toLowerCase()}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#0b1d3a] text-sky-400 flex items-center justify-center shrink-0">{item.icon}</div>
                  <div className="min-w-0">
                    <h3 className={labelCls}>{item.title}</h3>
                    {item.href ? (
                      <a href={item.href} className="mt-1 block font-semibold text-[#0b1d3a] hover:text-sky-600 transition-colors break-words"
                        onClick={() => item.track && trackEvent("call_click", "Contact Page Card")}>{item.detail}</a>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-slate-700">{item.detail}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form + sidebar */}
        <section className="py-10 lg:py-14">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-6">

              <div id="contact-form" className="lg:col-span-7 bg-white rounded-3xl ring-1 ring-slate-200 p-6 md:p-9 shadow-sm scroll-mt-24">
                <div className="flex items-end justify-between gap-4 mb-7 pb-6 border-b border-slate-100">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-600">Enquiry form</p>
                    <h2 className="mt-1 text-2xl md:text-3xl font-bold font-display text-[#0b1d3a]">Send Us a Message</h2>
                    <p className="text-slate-500 text-sm mt-1">We'll get back to you within the hour</p>
                  </div>
                  <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-600 items-center justify-center shrink-0">
                    <Send className="w-5 h-5" />
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-name" className={labelCls}>Full Name *</Label>
                      <Input id="contact-name" name="name" autoComplete="name" required value={formData.name} onChange={e => updateForm("name", e.target.value)} placeholder="e.g. John Smith" className={fieldCls} data-testid="input-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-phone" className={labelCls}>Phone Number</Label>
                      <Input id="contact-phone" name="phone" autoComplete="tel" value={formData.phone} onChange={e => updateForm("phone", e.target.value)} placeholder="e.g. 07700 900000" type="tel" className={fieldCls} data-testid="input-phone" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-email" className={labelCls}>Email Address *</Label>
                      <Input id="contact-email" name="email" autoComplete="email" required value={formData.email} onChange={e => updateForm("email", e.target.value)} placeholder="your@email.com" type="email" className={fieldCls} data-testid="input-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-subject" className={labelCls}>How Can We Help?</Label>
                      <select
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={e => updateForm("subject", e.target.value)}
                        data-testid="select-subject"
                        className="flex h-12 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Select an option...</option>
                        <option value="AC Repair">AC Repair</option>
                        <option value="Maintenance / Service">Maintenance / Service</option>
                        <option value="New Installation">New Installation</option>
                        <option value="Free Site Survey">Free Site Survey</option>
                        <option value="Installation Quote">Installation Quote</option>
                        <option value="General Enquiry">General Enquiry</option>
                        {SAUNA_ENQUIRY_OPTIONS.map(service => <option key={service} value={service}>{service}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="contact-message" className={labelCls}>Your Message *</Label>
                    <Textarea id="contact-message" name="message" required value={formData.message} onChange={e => updateForm("message", e.target.value)} placeholder="Tell us about your property, the issue you're experiencing, or what you'd like to discuss..." className="min-h-[140px] rounded-xl border-slate-200 bg-slate-50/70 focus-visible:bg-white" data-testid="input-message" />
                  </div>

                  <FileUploader files={files} onChange={setFiles} label="Attach photos, PDFs or Excel (optional)" />

                  <Button type="submit" size="lg" disabled={submitting} className="w-full text-base h-14 rounded-full bg-[#0b1d3a] hover:bg-[#13305c] text-white" data-testid="button-submit">
                    <Send className="w-5 h-5 mr-2" /> {submitting ? "Sending..." : "Send Message"}
                  </Button>

                  <p className="text-center text-sm text-slate-500">
                    By submitting you agree to be contacted by our team. Prefer to call? <a href="tel:02080870906" className="text-sky-700 font-semibold hover:underline" onClick={() => trackEvent("call_click", "Contact Form Inline")}>020 8087 0906</a>
                  </p>
                </form>
              </div>

              <aside className="lg:col-span-5 space-y-6">
                <div className="bg-white rounded-3xl ring-1 ring-slate-200 p-6 md:p-7 shadow-sm">
                  <div className="flex items-center gap-3 mb-5">
                    <Clock className="w-5 h-5 text-sky-600" />
                    <h3 className="text-lg font-bold font-display text-[#0b1d3a]">Opening Hours</h3>
                  </div>
                  <dl className="divide-y divide-slate-100 text-sm">
                    {[
                      ["Monday – Friday", "8:45am – 5:00pm"],
                      ["Saturday", "By appointment"],
                      ["Sunday", "Emergency only"],
                    ].map(([d, h]) => (
                      <div key={d} className="flex justify-between items-center py-2.5">
                        <dt className="text-slate-700">{d}</dt>
                        <dd className="font-semibold text-[#0b1d3a]">{h}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-4 flex items-start gap-3 rounded-2xl bg-red-50 p-4">
                    <span className="relative mt-1.5 flex w-2 h-2 shrink-0">
                      <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60" />
                      <span className="relative w-2 h-2 rounded-full bg-red-500" />
                    </span>
                    <p className="text-sm text-slate-800">
                      <span className="font-bold">24/7 Emergency Line</span> — out-of-hours emergency call-outs available on <a href="tel:02080870906" className="text-sky-700 font-semibold hover:underline" onClick={() => trackEvent("call_click", "Emergency Line")}>020 8087 0906</a>
                    </p>
                  </div>
                </div>

                <div className="relative overflow-hidden bg-[#0b1d3a] rounded-3xl p-6 md:p-7 text-white">
                  <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-sky-500/20 blur-2xl pointer-events-none" />
                  <h3 className="relative text-lg font-bold font-display mb-5">Why Choose Us?</h3>
                  <ul className="relative grid sm:grid-cols-2 lg:grid-cols-1 gap-3">
                    {[
                      { icon: <Shield className="w-4 h-4" />, text: "Fully licensed & F-Gas certified engineers" },
                      { icon: <Zap className="w-4 h-4" />, text: "Same-day emergency repair visits" },
                      { icon: <MapPin className="w-4 h-4" />, text: "Free site surveys for all installations" },
                      { icon: <Wrench className="w-4 h-4" />, text: "All major brands installed & serviced" },
                      { icon: <CheckCircle2 className="w-4 h-4" />, text: "No call-out charge on repairs" },
                      { icon: <Users className="w-4 h-4" />, text: "15+ years serving local customers" }
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-white/10 text-sky-300 flex items-center justify-center shrink-0">{item.icon}</span>
                        <span className="text-sm font-medium text-white/90">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-sky-500 rounded-3xl p-6 md:p-7 text-[#0b1d3a] flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium opacity-80">Prefer to speak to someone?</p>
                    <a href="tel:02080870906" className="block text-2xl md:text-3xl font-bold font-display hover:underline" onClick={() => trackEvent("call_click", "Contact CTA")} data-testid="link-cta-call">020 8087 0906</a>
                    <p className="text-sm opacity-80 mt-0.5">Mon-Fri · 8:45am – 5pm</p>
                  </div>
                  <span className="w-12 h-12 rounded-full bg-[#0b1d3a] text-sky-400 flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></span>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
